import React from 'react';

// Base pulse animation via inline style
const pulseStyle = {
  background: 'linear-gradient(90deg, rgba(255,255,255,0.04) 25%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0.04) 75%)',
  backgroundSize: '200% 100%',
  animation: 'skeletonPulse 1.6s ease-in-out infinite',
  borderRadius: 'var(--radius-sm)',
};

export const SkeletonBlock = ({ width = '100%', height = '16px', className = '', style = {} }) => (
  <div
    className={className}
    style={{ ...pulseStyle, width, height, ...style }}
  />
);

export const SkeletonCard = ({ children, className = '', style = {} }) => (
  <div
    className={`bg-card border-2 border-border rounded-[var(--radius-md)] p-4 sm:p-5 lg:p-6 ${className}`}
    style={{ position: 'relative', overflow: 'hidden', ...style }}
  >
    {children}
  </div>
);

// Spinner component — rust orange accent
export const LoadingSpinner = ({ size = 40, color = '#FF5722', className = '' }) => (
  <div className={className} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <svg
      width={size}
      height={size}
      viewBox="0 0 40 40"
      fill="none"
      style={{ animation: 'spin 0.9s linear infinite' }}
    >
      <circle cx="20" cy="20" r="16" stroke="rgba(255,87,34,0.15)" strokeWidth="3" />
      <path d="M20 4 A16 16 0 0 1 36 20" stroke={color} strokeWidth="3" strokeLinecap="round" />
    </svg>
  </div>
);

// Full-page loading overlay
export const PageLoadingOverlay = ({ label = 'Laster...' }) => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '60vh',
      gap: '20px',
    }}
  >
    <LoadingSpinner size={48} />
    <span style={{ fontFamily: 'var(--font-heading)', fontSize: '13px', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--color-muted-foreground)' }}>{label}</span>
  </div>
);

// Dashboard AccountStatus skeleton
export const AccountStatusSkeleton = () => (
  <SkeletonCard>
    <div className="flex items-center justify-between mb-6">
      <div>
        <SkeletonBlock width="140px" height="24px" style={{ marginBottom: '8px' }} />
        <SkeletonBlock width="80px" height="4px" />
      </div>
      <div className="flex gap-2">
        <SkeletonBlock width="90px" height="32px" />
        <SkeletonBlock width="80px" height="32px" />
      </div>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
      {[0,1,2,3]?.map(i => (
        <div key={i} className="p-4 bg-muted rounded-[var(--radius-sm)]">
          <div className="flex items-start gap-3">
            <SkeletonBlock width="48px" height="48px" style={{ borderRadius: 'var(--radius-sm)', flexShrink: 0 }} />
            <div className="flex-1">
              <SkeletonBlock width="60px" height="12px" style={{ marginBottom: '8px' }} />
              <SkeletonBlock width="100px" height="18px" />
            </div>
          </div>
        </div>
      ))}
    </div>
    <div className="flex gap-4 p-4 bg-muted/50 border-2 border-border rounded-[var(--radius-sm)] mb-4">
      <div className="flex-1">
        <SkeletonBlock width="80px" height="12px" style={{ marginBottom: '8px' }} />
        <SkeletonBlock width="120px" height="16px" />
      </div>
      <div className="flex-1">
        <SkeletonBlock width="100px" height="12px" style={{ marginBottom: '8px' }} />
        <SkeletonBlock width="160px" height="16px" />
      </div>
    </div>
    <SkeletonBlock width="100%" height="64px" style={{ borderRadius: 'var(--radius-sm)' }} />
  </SkeletonCard>
);

// Dashboard RoadConditions skeleton
export const RoadConditionsSkeleton = () => (
  <SkeletonCard>
    <div className="flex items-center justify-between mb-6">
      <div>
        <SkeletonBlock width="110px" height="24px" style={{ marginBottom: '8px' }} />
        <SkeletonBlock width="70px" height="4px" />
      </div>
      <SkeletonBlock width="100px" height="20px" />
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
      {[0,1,2]?.map(i => (
        <div key={i} className="p-4 bg-muted rounded-[var(--radius-sm)]">
          <div className="flex items-start justify-between mb-3">
            <div>
              <SkeletonBlock width="80px" height="18px" style={{ marginBottom: '6px' }} />
              <SkeletonBlock width="100px" height="14px" />
            </div>
            <SkeletonBlock width="48px" height="48px" style={{ borderRadius: 'var(--radius-sm)' }} />
          </div>
          <div className="flex gap-4">
            <SkeletonBlock width="60px" height="14px" />
            <SkeletonBlock width="60px" height="14px" />
          </div>
        </div>
      ))}
    </div>
  </SkeletonCard>
);

// Toll History trip card skeleton
export const TripCardSkeleton = ({ index = 0 }) => {
  const isOffset = index % 2 === 1;
  return (
    <div style={{ marginLeft: isOffset ? '24px' : '0px', marginRight: isOffset ? '0px' : '24px' }}>
      <div
        className="bg-card border-2 border-border rounded-[var(--radius-md)] p-5 md:p-6"
        style={{ position: 'relative', overflow: 'hidden' }}
      >
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="flex-1">
            <SkeletonBlock width="200px" height="20px" style={{ marginBottom: '8px' }} />
            <SkeletonBlock width="140px" height="14px" />
          </div>
          <SkeletonBlock width="80px" height="28px" style={{ borderRadius: 'var(--radius-sm)' }} />
        </div>
        <div className="p-3 bg-muted rounded-[var(--radius-sm)] mb-4">
          <SkeletonBlock width="100%" height="56px" style={{ borderRadius: 'var(--radius-sm)' }} />
        </div>
        <div className="flex items-center justify-between">
          <div className="flex gap-4">
            <SkeletonBlock width="80px" height="14px" />
            <SkeletonBlock width="80px" height="14px" />
          </div>
          <SkeletonBlock width="100px" height="32px" style={{ borderRadius: 'var(--radius-sm)' }} />
        </div>
      </div>
    </div>
  );
};

// Toll History stats skeleton
export const TripStatsSkeleton = () => (
  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-8">
    {[0,1,2,3]?.map(i => (
      <div key={i} className="bg-card border-2 border-border rounded-[var(--radius-md)] p-4">
        <SkeletonBlock width="40px" height="40px" style={{ borderRadius: 'var(--radius-sm)', marginBottom: '12px' }} />
        <SkeletonBlock width="80px" height="24px" style={{ marginBottom: '6px' }} />
        <SkeletonBlock width="100px" height="14px" />
      </div>
    ))}
  </div>
);

// Subscription portal overview skeleton
export const SubscriptionOverviewSkeleton = () => (
  <div className="card" style={{ position: 'relative', overflow: 'hidden' }}>
    <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-8">
      <div className="flex-1">
        <SkeletonBlock width="280px" height="36px" style={{ marginBottom: '10px' }} />
        <SkeletonBlock width="200px" height="16px" />
      </div>
      <SkeletonBlock width="100px" height="36px" style={{ borderRadius: 'var(--radius-sm)' }} />
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
      {[0,1,2]?.map(i => (
        <div key={i} className="p-4 md:p-6 rounded border-2 border-border bg-muted">
          <div className="flex items-center gap-3 mb-3">
            <SkeletonBlock width="48px" height="48px" style={{ borderRadius: 'var(--radius-sm)' }} />
            <SkeletonBlock width="100px" height="14px" />
          </div>
          <SkeletonBlock width="120px" height="28px" />
        </div>
      ))}
    </div>
    <div className="pt-6 border-t-2 border-border">
      <SkeletonBlock width="160px" height="20px" style={{ marginBottom: '16px' }} />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {[0,1,2,3]?.map(i => (
          <div key={i} className="flex items-center gap-3">
            <SkeletonBlock width="20px" height="20px" style={{ borderRadius: '50%', flexShrink: 0 }} />
            <SkeletonBlock width="200px" height="16px" />
          </div>
        ))}
      </div>
    </div>
  </div>
);

// Subscription portal invoice table skeleton
export const InvoiceTableSkeleton = () => (
  <div className="card" style={{ position: 'relative', overflow: 'hidden' }}>
    <SkeletonBlock width="200px" height="28px" style={{ marginBottom: '24px' }} />
    <div style={{ borderTop: '2px solid var(--color-border)' }}>
      {[0,1,2,3]?.map(i => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '16px 0', borderBottom: '1px solid var(--color-border)' }}>
          <SkeletonBlock width="120px" height="16px" />
          <SkeletonBlock width="90px" height="16px" />
          <SkeletonBlock width="80px" height="16px" style={{ marginLeft: 'auto' }} />
          <SkeletonBlock width="60px" height="24px" style={{ borderRadius: 'var(--radius-sm)' }} />
          <SkeletonBlock width="80px" height="32px" style={{ borderRadius: 'var(--radius-sm)' }} />
        </div>
      ))}
    </div>
  </div>
);

// Subscription portal payment method skeleton
export const PaymentMethodSkeleton = () => (
  <div className="card" style={{ position: 'relative', overflow: 'hidden' }}>
    <SkeletonBlock width="180px" height="28px" style={{ marginBottom: '24px' }} />
    <div style={{ padding: '24px', background: 'var(--color-muted)', borderRadius: 'var(--radius-sm)', marginBottom: '16px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '32px' }}>
        <SkeletonBlock width="60px" height="40px" style={{ borderRadius: '4px' }} />
        <SkeletonBlock width="80px" height="20px" />
      </div>
      <SkeletonBlock width="180px" height="22px" style={{ marginBottom: '16px' }} />
      <div style={{ display: 'flex', gap: '32px' }}>
        <SkeletonBlock width="80px" height="16px" />
        <SkeletonBlock width="80px" height="16px" />
      </div>
    </div>
    <div style={{ display: 'flex', gap: '12px' }}>
      <SkeletonBlock width="120px" height="40px" style={{ borderRadius: 'var(--radius-sm)' }} />
      <SkeletonBlock width="120px" height="40px" style={{ borderRadius: 'var(--radius-sm)' }} />
    </div>
  </div>
);

// Subscription plans skeleton
export const SubscriptionPlansSkeleton = () => (
  <div>
    <SkeletonBlock width="200px" height="32px" style={{ marginBottom: '8px' }} />
    <SkeletonBlock width="280px" height="16px" style={{ marginBottom: '32px' }} />
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {[0,1,2]?.map(i => (
        <div key={i} style={{ padding: '28px', background: 'var(--color-card)', border: '2px solid var(--color-border)', borderRadius: 'var(--radius-md)', position: 'relative', overflow: 'hidden' }}>
          <SkeletonBlock width="80px" height="20px" style={{ marginBottom: '8px' }} />
          <SkeletonBlock width="140px" height="14px" style={{ marginBottom: '20px' }} />
          <SkeletonBlock width="100px" height="36px" style={{ marginBottom: '24px' }} />
          {[0,1,2,3]?.map(j => (
            <div key={j} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
              <SkeletonBlock width="16px" height="16px" style={{ borderRadius: '50%', flexShrink: 0 }} />
              <SkeletonBlock width={`${140 + j * 20}px`} height="14px" />
            </div>
          ))}
          <SkeletonBlock width="100%" height="44px" style={{ borderRadius: 'var(--radius-sm)', marginTop: '24px' }} />
        </div>
      ))}
    </div>
  </div>
);

// Vehicle management skeleton
export const VehicleManagementSkeleton = () => (
  <div className="card" style={{ position: 'relative', overflow: 'hidden' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
      <SkeletonBlock width="160px" height="28px" />
      <SkeletonBlock width="120px" height="40px" style={{ borderRadius: 'var(--radius-sm)' }} />
    </div>
    {[0,1]?.map(i => (
      <div key={i} style={{ padding: '20px', background: 'var(--color-muted)', borderRadius: 'var(--radius-sm)', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '16px' }}>
        <SkeletonBlock width="48px" height="48px" style={{ borderRadius: 'var(--radius-sm)', flexShrink: 0 }} />
        <div style={{ flex: 1 }}>
          <SkeletonBlock width="120px" height="18px" style={{ marginBottom: '6px' }} />
          <SkeletonBlock width="80px" height="14px" />
        </div>
        <SkeletonBlock width="60px" height="24px" style={{ borderRadius: 'var(--radius-sm)' }} />
        <SkeletonBlock width="80px" height="32px" style={{ borderRadius: 'var(--radius-sm)' }} />
      </div>
    ))}
  </div>
);

// Notification settings skeleton
export const NotificationSettingsSkeleton = () => (
  <div className="card" style={{ position: 'relative', overflow: 'hidden' }}>
    <SkeletonBlock width="200px" height="28px" style={{ marginBottom: '24px' }} />
    {[0,1,2]?.map(section => (
      <div key={section} style={{ marginBottom: '28px' }}>
        <SkeletonBlock width="120px" height="16px" style={{ marginBottom: '16px' }} />
        {[0,1,2]?.map(i => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid var(--color-border)' }}>
            <div>
              <SkeletonBlock width="160px" height="16px" style={{ marginBottom: '4px' }} />
              <SkeletonBlock width="220px" height="12px" />
            </div>
            <SkeletonBlock width="44px" height="24px" style={{ borderRadius: '12px', flexShrink: 0 }} />
          </div>
        ))}
      </div>
    ))}
  </div>
);

// Customer Stories hero skeleton
export const StoriesHeroSkeleton = () => (
  <section style={{ padding: '80px 24px 60px', maxWidth: '1200px', margin: '0 auto' }}>
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '20px', marginBottom: '48px' }}>
      <div style={{ width: '4px', height: '60px', background: 'rgba(255,87,34,0.2)', borderRadius: '2px', flexShrink: 0 }} />
      <div style={{ flex: 1 }}>
        <SkeletonBlock width="120px" height="12px" style={{ marginBottom: '16px' }} />
        <SkeletonBlock width="70%" height="48px" style={{ marginBottom: '12px' }} />
        <SkeletonBlock width="50%" height="20px" />
      </div>
    </div>
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '24px' }}>
      {[0,1,2]?.map(i => (
        <div key={i} style={{ padding: '28px', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(224,228,232,0.08)', borderRadius: '2px' }}>
          <SkeletonBlock width="60px" height="40px" style={{ marginBottom: '12px' }} />
          <SkeletonBlock width="80px" height="14px" />
        </div>
      ))}
    </div>
  </section>
);

// Client logos skeleton
export const ClientLogosSkeleton = () => (
  <section style={{ padding: '48px 24px', maxWidth: '1200px', margin: '0 auto' }}>
    <SkeletonBlock width="160px" height="16px" style={{ margin: '0 auto 32px' }} />
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px' }}>
      {[0,1,2,3,4,5,6,7]?.map(i => (
        <div key={i} style={{ padding: '20px', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(224,228,232,0.06)', borderRadius: '2px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <SkeletonBlock width="80px" height="32px" />
        </div>
      ))}
    </div>
  </section>
);

// Case study card skeleton
export const CaseStudySkeleton = () => (
  <div style={{ padding: '32px', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(224,228,232,0.08)', borderRadius: '2px', marginBottom: '24px' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
      <SkeletonBlock width="56px" height="56px" style={{ borderRadius: '2px', flexShrink: 0 }} />
      <div>
        <SkeletonBlock width="140px" height="20px" style={{ marginBottom: '6px' }} />
        <SkeletonBlock width="100px" height="14px" />
      </div>
    </div>
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
      <div style={{ padding: '16px', background: 'rgba(255,87,34,0.05)', border: '1px solid rgba(255,87,34,0.1)', borderRadius: '2px' }}>
        <SkeletonBlock width="80px" height="12px" style={{ marginBottom: '8px' }} />
        <SkeletonBlock width="100px" height="28px" />
      </div>
      <div style={{ padding: '16px', background: 'rgba(0,79,152,0.05)', border: '1px solid rgba(0,79,152,0.1)', borderRadius: '2px' }}>
        <SkeletonBlock width="80px" height="12px" style={{ marginBottom: '8px' }} />
        <SkeletonBlock width="100px" height="28px" />
      </div>
    </div>
    <SkeletonBlock width="100%" height="60px" style={{ marginBottom: '16px' }} />
    <SkeletonBlock width="80%" height="14px" />
  </div>
);

// Testimonials skeleton
export const TestimonialsSkeleton = () => (
  <section style={{ padding: '60px 24px', maxWidth: '1200px', margin: '0 auto' }}>
    <SkeletonBlock width="200px" height="32px" style={{ marginBottom: '8px' }} />
    <SkeletonBlock width="300px" height="16px" style={{ marginBottom: '40px' }} />
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '24px' }}>
      {[0,1,2,3]?.map(i => (
        <div key={i} style={{ padding: '28px', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(224,228,232,0.08)', borderRadius: '2px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
            <SkeletonBlock width="56px" height="56px" style={{ borderRadius: '50%', flexShrink: 0 }} />
            <div>
              <SkeletonBlock width="120px" height="18px" style={{ marginBottom: '6px' }} />
              <SkeletonBlock width="90px" height="14px" />
            </div>
          </div>
          <SkeletonBlock width="100%" height="14px" style={{ marginBottom: '8px' }} />
          <SkeletonBlock width="90%" height="14px" style={{ marginBottom: '8px' }} />
          <SkeletonBlock width="70%" height="14px" />
        </div>
      ))}
    </div>
  </section>
);

// About Us hero skeleton
export const AboutHeroSkeleton = () => (
  <section style={{ padding: '80px 24px 60px', maxWidth: '1200px', margin: '0 auto' }}>
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '20px', marginBottom: '48px' }}>
      <div style={{ width: '4px', height: '60px', background: 'rgba(255,87,34,0.2)', borderRadius: '2px', flexShrink: 0 }} />
      <div style={{ flex: 1 }}>
        <SkeletonBlock width="100px" height="12px" style={{ marginBottom: '16px' }} />
        <SkeletonBlock width="60%" height="52px" style={{ marginBottom: '16px' }} />
        <SkeletonBlock width="80%" height="18px" style={{ marginBottom: '8px' }} />
        <SkeletonBlock width="65%" height="18px" />
      </div>
    </div>
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '24px' }}>
      {[0,1,2]?.map(i => (
        <div key={i} style={{ padding: '24px', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(224,228,232,0.08)', borderRadius: '2px' }}>
          <SkeletonBlock width="48px" height="48px" style={{ borderRadius: '2px', marginBottom: '16px' }} />
          <SkeletonBlock width="100px" height="20px" style={{ marginBottom: '8px' }} />
          <SkeletonBlock width="140px" height="14px" />
        </div>
      ))}
    </div>
  </section>
);

// Team section skeleton
export const TeamSkeleton = () => (
  <section style={{ padding: '60px 24px', maxWidth: '1200px', margin: '0 auto' }}>
    <SkeletonBlock width="160px" height="32px" style={{ marginBottom: '8px' }} />
    <SkeletonBlock width="280px" height="16px" style={{ marginBottom: '40px' }} />
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '24px' }}>
      {[0,1,2,3,4,5]?.map(i => (
        <div key={i} style={{ padding: '24px', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(224,228,232,0.08)', borderRadius: '2px', textAlign: 'center' }}>
          <SkeletonBlock width="80px" height="80px" style={{ borderRadius: '50%', margin: '0 auto 16px' }} />
          <SkeletonBlock width="120px" height="18px" style={{ margin: '0 auto 8px' }} />
          <SkeletonBlock width="90px" height="14px" style={{ margin: '0 auto 12px' }} />
          <SkeletonBlock width="100%" height="12px" style={{ marginBottom: '6px' }} />
          <SkeletonBlock width="80%" height="12px" style={{ margin: '0 auto' }} />
        </div>
      ))}
    </div>
  </section>
);

// FAQ skeleton
export const FAQSkeleton = () => (
  <section style={{ padding: '60px 24px', maxWidth: '1200px', margin: '0 auto' }}>
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '20px', marginBottom: '40px' }}>
      <div style={{ width: '4px', height: '56px', background: 'rgba(255,87,34,0.2)', borderRadius: '2px', flexShrink: 0 }} />
      <div style={{ flex: 1 }}>
        <SkeletonBlock width="100px" height="12px" style={{ marginBottom: '16px' }} />
        <SkeletonBlock width="55%" height="44px" style={{ marginBottom: '16px' }} />
        <SkeletonBlock width="70%" height="18px" />
      </div>
    </div>
    {/* Search bar skeleton */}
    <SkeletonBlock width="100%" height="52px" style={{ borderRadius: 'var(--radius-sm)', marginBottom: '24px' }} />
    {/* Category tabs skeleton */}
    <div style={{ display: 'flex', gap: '8px', marginBottom: '32px' }}>
      {[0,1,2,3,4]?.map(i => (
        <SkeletonBlock key={i} width={`${70 + i * 15}px`} height="36px" style={{ borderRadius: 'var(--radius-sm)' }} />
      ))}
    </div>
    {/* Accordion items skeleton */}
    {[0,1,2,3,4]?.map(i => (
      <div key={i} style={{ padding: '20px', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(224,228,232,0.08)', borderRadius: '2px', marginBottom: '8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <SkeletonBlock width={`${50 + i * 8}%`} height="18px" />
        <SkeletonBlock width="20px" height="20px" style={{ borderRadius: '50%', flexShrink: 0 }} />
      </div>
    ))}
  </section>
);

// Services Detail skeleton
export const ServicesDetailSkeleton = () => (
  <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '80px 24px 60px' }}>
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '20px', marginBottom: '48px' }}>
      <div style={{ width: '4px', height: '60px', background: 'rgba(0,79,152,0.2)', borderRadius: '2px', flexShrink: 0 }} />
      <div style={{ flex: 1 }}>
        <SkeletonBlock width="120px" height="12px" style={{ marginBottom: '16px' }} />
        <SkeletonBlock width="65%" height="52px" style={{ marginBottom: '16px' }} />
        <SkeletonBlock width="80%" height="18px" />
      </div>
    </div>
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '24px', marginBottom: '48px' }}>
      {[0,1,2]?.map(i => (
        <div key={i} style={{ padding: '28px', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(224,228,232,0.08)', borderRadius: '2px' }}>
          <SkeletonBlock width="48px" height="48px" style={{ borderRadius: '2px', marginBottom: '16px' }} />
          <SkeletonBlock width="120px" height="20px" style={{ marginBottom: '10px' }} />
          <SkeletonBlock width="100%" height="14px" style={{ marginBottom: '6px' }} />
          <SkeletonBlock width="85%" height="14px" style={{ marginBottom: '6px' }} />
          <SkeletonBlock width="70%" height="14px" />
        </div>
      ))}
    </div>
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '32px' }}>
      <div>
        {[0,1,2,3]?.map(i => (
          <div key={i} style={{ display: 'flex', gap: '16px', marginBottom: '20px' }}>
            <SkeletonBlock width="40px" height="40px" style={{ borderRadius: '2px', flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <SkeletonBlock width="140px" height="18px" style={{ marginBottom: '8px' }} />
              <SkeletonBlock width="100%" height="14px" />
            </div>
          </div>
        ))}
      </div>
      <SkeletonBlock width="100%" height="280px" style={{ borderRadius: '2px' }} />
    </div>
  </div>
);

// Pricing page skeleton
export const PricingHeroSkeleton = () => (
  <section style={{ padding: '80px 48px 60px', maxWidth: '1440px', margin: '0 auto' }}>
    <div style={{ textAlign: 'center', marginBottom: '48px' }}>
      <SkeletonBlock width="120px" height="12px" style={{ margin: '0 auto 20px' }} />
      <SkeletonBlock width="50%" height="56px" style={{ margin: '0 auto 16px' }} />
      <SkeletonBlock width="35%" height="20px" style={{ margin: '0 auto 40px' }} />
      {/* Toggle skeleton */}
      <SkeletonBlock width="200px" height="44px" style={{ margin: '0 auto', borderRadius: '22px' }} />
    </div>
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '24px', maxWidth: '900px', margin: '0 auto' }}>
      {[0,1]?.map(i => (
        <div key={i} style={{ padding: '36px', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(224,228,232,0.08)', borderRadius: '2px' }}>
          <SkeletonBlock width="80px" height="20px" style={{ marginBottom: '8px' }} />
          <SkeletonBlock width="140px" height="14px" style={{ marginBottom: '24px' }} />
          <SkeletonBlock width="120px" height="44px" style={{ marginBottom: '28px' }} />
          {[0,1,2,3,4]?.map(j => (
            <div key={j} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
              <SkeletonBlock width="18px" height="18px" style={{ borderRadius: '50%', flexShrink: 0 }} />
              <SkeletonBlock width={`${120 + j * 20}px`} height="14px" />
            </div>
          ))}
          <SkeletonBlock width="100%" height="48px" style={{ borderRadius: '2px', marginTop: '28px' }} />
        </div>
      ))}
    </div>
  </section>
);

export default SkeletonBlock;
